import { useState } from "react";

import {
  BottomModalSection,
  HeaderModalSection,
} from "@hl/base-components/lib/modal/ModalSection";
import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { useAuth } from "@hl/shared-features/lib/features/auth/AuthContext";
import { Box, Button, Group, Image, Space, Text, Title } from "@mantine/core";
import { ImageIcon } from "~/../../base-components/lib/assets/icons.generated";
import { useModalStack } from "~/../../shared-features/lib/features/modal";
import { networkByNameLookup } from "~/../../shared-features/lib/utils/blockExplorer";

import { CollectionStandardType, Condition } from "apollo/graphql.generated";

import PreviewCollectionTile from "./PreviewCollectionTile";
import SelectionMethodTabs, { TabName } from "./SelectionMethodTabs";
import ByContractOrUrlTab, {
  SelectedNftContractCondition,
} from "./tabs/ByContractOrUrlTab";
import CreatedOnHighlightTab, {
  SelectedHihglightCondition,
} from "./tabs/CreatedOnHighlightTab";
import WalletTab, { SelectedWalletCondition } from "./tabs/WalletTab";

export type FindTokenProps = {
  onSave: (data: Condition) => void;
  submitText?: string;
  isImportModal?: boolean;
  initialCondition?: Condition;
};

const FindToken = ({ onSave, initialCondition }: FindTokenProps) => {
  const { pushModal, popModal } = useModalStack();
  const { walletAddress } = useAuth();

  const [activeTab, setActiveTab] = useState<TabName>("CONTRACT_TAB");

  const handleSelectedHighlightCondition = (
    item?: SelectedHihglightCondition
  ) => {
    if (item == null) return;
    openPreviewConditionModal(item.condition);
  };

  const handleSelectedWalletCondition = (item?: SelectedWalletCondition) => {
    if (item == null) return;
    openPreviewConditionModal(item.condition);
  };

  const handleSelectedNftContractCondition = (
    item?: SelectedNftContractCondition
  ) => {
    if (item == null) return;
    openPreviewConditionModal(item.condition);
  };

  const openPreviewConditionModal = (condition?: Condition) => {
    if (condition == null) return;

    pushModal(
      <ConditionPreviewModal
        condition={condition}
        onSave={(amount: number) => {
          popModal();
          onSave({ ...condition, amount });
        }}
      />,
      {
        size: "xl",
        padding: 16,
        closeOnClickOutside: false,
        onClose: () => {
          popModal();
          popModal();
        },
        title: (
          <Group spacing="xs">
            {/* <ActionIcon onClick={popModal}>
              <ArrowLeft />
            </ActionIcon> */}

            <Title size={18}>Set token</Title>
          </Group>
        ),
      }
    );
  };

  return (
    <>
      <HeaderModalSection>
        <SelectionMethodTabs
          changeTab={setActiveTab}
          activeTab={activeTab}
          showCreatedTab={true}
          showContractTab={true}
          showWalletTab={!!walletAddress}
        />
      </HeaderModalSection>

      <Box mih={300}>
        {activeTab === "CREATED_TAB" && (
          <CreatedOnHighlightTab
            mode="TOKEN"
            onSelected={handleSelectedHighlightCondition}
          />
        )}

        {activeTab === "WALLET_TAB" && (
          <WalletTab mode="TOKEN" onSelected={handleSelectedWalletCondition} />
        )}

        {activeTab === "CONTRACT_TAB" && (
          <ByContractOrUrlTab
            initialCondition={initialCondition}
            mode="TOKEN"
            onSelected={handleSelectedNftContractCondition}
          />
        )}
      </Box>
    </>
  );
};

export default FindToken;

const ConditionPreviewModal = ({
  condition,
  onSave,
}: {
  condition: Condition;
  onSave: (value: number) => void;
}) => {
  const { popModal } = useModalStack();

  console.log("asdf", condition);

  const imageUrl = condition.data?.imageUrl || "";
  const name = condition.data?.name || "";
  const standard = condition.data?.standard || "";
  const networkDetails = networkByNameLookup(condition.data?.network);
  const details =
    condition.data?.collectionName != null
      ? `${networkDetails.displayName} ∙ ${condition.data.collectionName}`
      : standard === CollectionStandardType.OPENSEA
      ? "OpenSea"
      : `${networkDetails.displayName} ∙ ${standard}`;

  // const items = "505 items";

  return (
    <Box>
      <PreviewCollectionTile
        image={
          <Image
            src={imageUrl}
            fit="contain"
            width={68}
            height={68}
            radius={5}
            withPlaceholder
            placeholder={<ImageIcon width={24} height={24} />}
          ></Image>
        }
        title={
          <Text fw={500} size={"sm"} lineClamp={2}>
            {name}
          </Text>
        }
        subtitle={
          <>
            <Text size={"xs"} color={TEXT_COLOR.SECONDARY} lineClamp={2}>
              {details}
            </Text>
          </>
        }
        onEdit={() => {
          popModal();
        }}
      />

      <Space h="sm" />
      <BottomModalSection>
        <Button
          size="xl"
          fullWidth
          onClick={() => {
            onSave(condition.amount ?? 1);
          }}
        >
          Add condition
        </Button>
      </BottomModalSection>
    </Box>
  );
};
